// =============================================
// useCart Hook - React hook for the new persistent cart system
// =============================================
// This hook provides a clean interface for managing the cart state
// while synchronizing with the backend API.
// =============================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import cartService from '../services/cartService';
import { ChefHat, CheckCircle, ShoppingCart, Utensils } from 'lucide-react';
import React from 'react';

export const useCart = () => {
  // Cart state from backend
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch cart from backend
  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const data = await cartService.getCart();
      setCart(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching cart:', err);
      setError(err.message);
      // Initialize empty cart on error
      setCart({ items: [], total: 0, table_id: null, table_number: null });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load cart on mount
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Add item to cart
  const addItem = useCallback(async (product, options = {}) => {
    try {
      setActionLoading(true);
      
      const item = {
        product_id: product.id || product.product_id,
        product_name: product.name || product.product_name,
        unit_price: product.price || product.unit_price || product.selling_price,
        quantity: options.quantity || 1,
        destination: product.destination || options.destination || 'cuisine',
        category: product.category || options.category,
        supplements: options.supplements || [],
        accompaniment: options.accompaniment || null,
        notes: options.notes || '',
        // Formula-specific fields
        is_formula: options.is_formula || false,
        selected_items: options.selected_items || null,
        all_items: options.all_items || null
      };

      const updatedCart = await cartService.addCartItem(item);
      setCart(updatedCart);
      
      return updatedCart;
    } catch (err) {
      console.error('Error adding item:', err);
      toast.error('Erreur lors de l\'ajout');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Update item quantity
  const updateItemQuantity = useCallback(async (itemId, quantity) => {
    if (quantity <= 0) {
      return removeItem(itemId);
    }
    
    try {
      setActionLoading(true);
      const updatedCart = await cartService.updateCartItem(itemId, { quantity });
      setCart(updatedCart);
      return updatedCart;
    } catch (err) {
      console.error('Error updating item:', err);
      toast.error('Erreur lors de la modification');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Remove item from cart
  const removeItem = useCallback(async (itemId) => {
    try {
      setActionLoading(true);
      const updatedCart = await cartService.removeCartItem(itemId);
      setCart(updatedCart);
      toast.info('Article retiré');
      return updatedCart;
    } catch (err) {
      console.error('Error removing item:', err);
      toast.error('Erreur lors de la suppression');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Clear entire cart
  const clearCartItems = useCallback(async () => {
    try {
      setActionLoading(true);
      const updatedCart = await cartService.clearCart();
      setCart(updatedCart);
      toast.info('Panier vidé');
      return updatedCart;
    } catch (err) {
      console.error('Error clearing cart:', err);
      toast.error('Erreur lors du vidage');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Link cart to table
  // clearIfEmptyTable: If true and table is empty/free, clears the cart before linking
  const linkToTable = useCallback(async (tableId, clearIfEmptyTable = true) => {
    try {
      setActionLoading(true);
      const result = await cartService.linkCartToTable(tableId, clearIfEmptyTable);
      setCart(result.cart);
      
      // Show appropriate toast based on whether cart was cleared
      if (result.cart_cleared) {
        toast.success(`Table ${result.table?.number || ''} sélectionnée - Nouveau panier`, {
          icon: React.createElement(Utensils, { className: "w-5 h-5 text-green-500" })
        });
      } else {
        toast.success(result.message, {
          icon: React.createElement(Utensils, { className: "w-5 h-5 text-blue-500" })
        });
      }
      
      return result;
    } catch (err) {
      console.error('Error linking to table:', err);
      toast.error('Erreur lors de la liaison');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Unlink cart from table
  const unlinkFromTable = useCallback(async () => {
    try {
      setActionLoading(true);
      const result = await cartService.unlinkCartFromTable();
      setCart(result.cart);
      toast.info('Table désélectionnée');
      return result;
    } catch (err) {
      console.error('Error unlinking from table:', err);
      toast.error('Erreur');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Send to kitchen
  const sendToKitchen = useCallback(async (options = {}) => {
    try {
      setActionLoading(true);
      const result = await cartService.sendToKitchen(options);
      
      // Cart is cleared after sending
      setCart({ items: [], total: 0, table_id: null, table_number: null });
      
      toast.success(result.message, {
        icon: React.createElement(ChefHat, { className: "w-5 h-5 text-orange-500" })
      });
      
      return result;
    } catch (err) {
      console.error('Error sending to kitchen:', err);
      toast.error(err.response?.data?.detail || 'Erreur envoi cuisine');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Direct sale (counter/takeaway)
  const processDirectSale = useCallback(async (paymentDetails) => {
    try {
      setActionLoading(true);
      const result = await cartService.processDirectSale(paymentDetails);
      
      // Cart is cleared after sale
      setCart({ items: [], total: 0, table_id: null, table_number: null });
      
      toast.success(result.message, {
        icon: React.createElement(CheckCircle, { className: "w-5 h-5 text-green-500" })
      });
      
      return result;
    } catch (err) {
      console.error('Error processing direct sale:', err);
      toast.error(err.response?.data?.detail || 'Erreur vente directe');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Load drafts from a table into cart
  const loadFromTable = useCallback(async (tableId) => {
    try {
      setActionLoading(true);
      const result = await cartService.loadDraftsFromTable(tableId);
      setCart(result.cart);
      
      toast.info(result.message, {
        icon: React.createElement(ShoppingCart, { className: "w-5 h-5 text-blue-500" })
      });
      
      return result;
    } catch (err) {
      console.error('Error loading from table:', err);
      toast.error('Erreur chargement');
      throw err;
    } finally {
      setActionLoading(false);
    }
  }, []);

  // Computed values
  const cartItems = useMemo(() => cart?.items || [], [cart]);
  const cartTotal = useMemo(() => cart?.total || 0, [cart]);
  const itemCount = useMemo(() => {
    return cartItems.reduce((count, item) => count + (item.quantity || 1), 0);
  }, [cartItems]);
  const linkedTableId = useMemo(() => cart?.table_id || null, [cart]);
  const linkedTableNumber = useMemo(() => cart?.table_number || null, [cart]);
  const isEmpty = useMemo(() => cartItems.length === 0, [cartItems]);

  return {
    // State
    cart,
    cartItems,
    cartTotal,
    itemCount,
    linkedTableId,
    linkedTableNumber,
    isEmpty,
    loading,
    actionLoading,
    error,

    // Actions
    fetchCart,
    addItem,
    updateItemQuantity,
    removeItem,
    clearCartItems,
    linkToTable,
    unlinkFromTable,
    sendToKitchen,
    processDirectSale,
    loadFromTable,

    // Direct cart setter for optimistic updates
    setCart
  };
};

export default useCart;
